<template>
  <v-card class="elevation-2 rounded-lg my-5">
    <v-card-title class="py-4 primary--text">
      <v-icon left color="primary">mdi-format-list-checks</v-icon>
      TO-DO
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4">
      <!-- Botón para agregar nueva TO-DO -->
      <div v-if="canCreateTodo" class="d-flex justify-end mb-4">
        <v-btn color="primary" @click="showDialog(null)">
          <v-icon left>mdi-plus</v-icon>
          Nueva TO-DO
        </v-btn>
      </div>

      <!-- Lista de categorías -->
      <v-row v-if="categories.length > 0">
        <v-col
          v-for="category in categories"
          :key="category.id"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <v-card outlined hover class="category-card">
            <v-card-text class="pa-4">
              <div class="d-flex flex-column align-center">
                <!-- Preview del icono circular -->
                <v-avatar
                  :color="category.button_color"
                  size="80"
                  class="mb-3"
                >
                  <span
                    class="text-h4 font-weight-bold"
                    :style="{ color: category.text_color }"
                  >
                    {{ getInitial(category.name) }}
                  </span>
                </v-avatar>

                <!-- Nombre de la categoría -->
                <div class="text-h6 font-weight-medium text-center mb-2">
                  {{ category.name }}
                </div>

                <!-- Colores -->
                <div class="d-flex align-center mb-3">
                  <v-chip small outlined class="mr-1">
                    <v-icon left small :color="category.button_color">mdi-palette</v-icon>
                    Botón
                  </v-chip>
                  <v-chip small outlined>
                    <v-icon left small :color="category.text_color">mdi-format-color-text</v-icon>
                    Texto
                  </v-chip>
                </div>

                <!-- Acciones -->
                <div class="d-flex justify-center">
                  <v-btn v-if="canEditTodo" icon small color="primary" @click="showDialog(category)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn v-if="canDeleteTodo" icon small color="error" @click="confirmDelete(category)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Mensaje si no hay categorías -->
      <v-alert v-else type="info" outlined class="text-center">
        <div class="text-h6 mb-2">No hay TO-DO creadas</div>
        <p v-if="canCreateTodo" class="mb-0">Crea tu primera TO-DO para organizar tus tareas</p>
        <p v-else class="mb-0">Aún no se han creado TO-DO en el sistema</p>
      </v-alert>
    </v-card-text>

    <!-- Diálogo para crear/editar categoría -->
    <v-dialog v-model="dialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline primary--text">
          <v-icon left color="primary">mdi-format-list-checks</v-icon>
          {{ editedCategory.id ? 'Editar TO-DO' : 'Nueva TO-DO' }}
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-4">
          <v-form ref="form" v-model="valid">
            <!-- Preview del icono -->
            <div class="d-flex justify-center mb-4">
              <v-avatar
                :color="editedCategory.button_color"
                size="100"
              >
                <span
                  class="text-h3 font-weight-bold"
                  :style="{ color: editedCategory.text_color }"
                >
                  {{ getInitial(editedCategory.name) }}
                </span>
              </v-avatar>
            </div>

            <!-- Nombre -->
            <v-text-field
              v-model="editedCategory.name"
              :rules="nameRules"
              label="Nombre de la TO-DO"
              outlined
              dense
              prepend-inner-icon="mdi-tag"
              placeholder="Ej: Taller, Ventas, Logística"
              hide-details="auto"
              class="mb-4"
              @input="updatePreview"
            ></v-text-field>

            <!-- Color del botón -->
            <div class="mb-4">
              <label class="text-subtitle-2 grey--text text--darken-2 mb-2 d-block">
                Color del botón
              </label>
              <div class="d-flex align-center">
                <v-menu
                  v-model="colorPickerButton"
                  :close-on-content-click="false"
                  offset-y
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      :color="editedCategory.button_color"
                      dark
                      v-bind="attrs"
                      v-on="on"
                      class="mr-2"
                    >
                      <v-icon left>mdi-palette</v-icon>
                      Seleccionar color
                    </v-btn>
                  </template>
                  <v-color-picker
                    v-model="editedCategory.button_color"
                    mode="hexa"
                    hide-mode-switch
                  ></v-color-picker>
                </v-menu>
                <v-chip :color="editedCategory.button_color" dark>
                  {{ editedCategory.button_color }}
                </v-chip>
              </div>
            </div>

            <!-- Color del texto -->
            <div class="mb-4">
              <label class="text-subtitle-2 grey--text text--darken-2 mb-2 d-block">
                Color del texto
              </label>
              <div class="d-flex align-center">
                <v-menu
                  v-model="colorPickerText"
                  :close-on-content-click="false"
                  offset-y
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      :color="editedCategory.text_color"
                      dark
                      v-bind="attrs"
                      v-on="on"
                      class="mr-2"
                    >
                      <v-icon left>mdi-format-color-text</v-icon>
                      Seleccionar color
                    </v-btn>
                  </template>
                  <v-color-picker
                    v-model="editedCategory.text_color"
                    mode="hexa"
                    hide-mode-switch
                  ></v-color-picker>
                </v-menu>
                <v-chip :color="editedCategory.text_color" dark>
                  {{ editedCategory.text_color }}
                </v-chip>
              </div>
            </div>

            <!-- Colores predefinidos -->
            <div>
              <label class="text-subtitle-2 grey--text text--darken-2 mb-2 d-block">
                Colores predefinidos
              </label>
              <div class="d-flex flex-wrap">
                <v-btn
                  v-for="preset in colorPresets"
                  :key="preset.name"
                  :color="preset.button_color"
                  small
                  class="mr-2 mb-2"
                  @click="applyPreset(preset)"
                >
                  <span :style="{ color: preset.text_color }">
                    {{ preset.name }}
                  </span>
                </v-btn>
              </div>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">Cancelar</v-btn>
          <v-btn
            color="primary"
            :disabled="!valid"
            :loading="saving"
            @click="saveCategory"
          >
            <v-icon left>mdi-content-save</v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo de confirmación para eliminar -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline error--text">
          <v-icon color="error" left>mdi-alert-circle</v-icon>
          Confirmar Eliminación
        </v-card-title>

        <v-card-text class="py-4">
          ¿Estás seguro de que deseas eliminar la TO-DO <strong>{{ categoryToDelete?.name }}</strong>?
          <p class="caption red--text mt-2">Esta acción no se puede deshacer.</p>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="error" text @click="deleteCategory" :loading="deleting">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import TodoCategoryService from '@/services/TodoCategoryService'
import { mapGetters } from 'vuex'

export default {
  name: 'TodoCategorySettings',
  
  data: () => ({
    categories: [],
    loading: false,
    saving: false,
    deleting: false,
    dialog: false,
    deleteDialog: false,
    valid: false,
    colorPickerButton: false,
    colorPickerText: false,
    categoryToDelete: null,
    
    editedCategory: {
      id: null,
      name: '',
      button_color: '#3B82F6',
      text_color: '#FFFFFF'
    },
    
    defaultCategory: {
      id: null,
      name: '',
      button_color: '#3B82F6',
      text_color: '#FFFFFF'
    },
    
    nameRules: [
      v => !!v || 'El nombre es requerido',
      v => (v && v.length >= 2) || 'El nombre debe tener al menos 2 caracteres',
      v => (v && v.length <= 50) || 'El nombre debe tener máximo 50 caracteres'
    ],
    
    colorPresets: [
      { name: 'Azul', button_color: '#3B82F6', text_color: '#FFFFFF' },
      { name: 'Verde', button_color: '#10B981', text_color: '#FFFFFF' },
      { name: 'Rojo', button_color: '#EF4444', text_color: '#FFFFFF' },
      { name: 'Amarillo', button_color: '#F59E0B', text_color: '#000000' },
      { name: 'Púrpura', button_color: '#8B5CF6', text_color: '#FFFFFF' },
      { name: 'Rosa', button_color: '#EC4899', text_color: '#FFFFFF' },
      { name: 'Gris', button_color: '#6B7280', text_color: '#FFFFFF' },
      { name: 'Naranja', button_color: '#F97316', text_color: '#FFFFFF' }
    ]
  }),
  
  computed: {
    ...mapGetters(['HAS_PERMISSION']),
    
    canCreateTodo() {
      return this.HAS_PERMISSION('crear_todo')
    },
    
    canEditTodo() {
      return this.HAS_PERMISSION('editar_todo')
    },
    
    canDeleteTodo() {
      return this.HAS_PERMISSION('eliminar_todo')
    }
  },
  
  methods: {
    getInitial(name) {
      if (!name || name.length === 0) return '?'
      return name.charAt(0).toUpperCase()
    },
    
    showDialog(category) {
      if (category) {
        this.editedCategory = { ...category }
      } else {
        this.editedCategory = { ...this.defaultCategory }
      }
      this.dialog = true
    },
    
    closeDialog() {
      this.dialog = false
      this.$refs.form.reset()
      this.editedCategory = { ...this.defaultCategory }
    },
    
    updatePreview() {
      // Forzar actualización del preview
      this.$forceUpdate()
    },
    
    applyPreset(preset) {
      this.editedCategory.button_color = preset.button_color
      this.editedCategory.text_color = preset.text_color
    },
    
    async loadCategories() {
      this.loading = true
      try {
        const response = await TodoCategoryService.get()
        
        if (response.data && response.data.data) {
          // Soporta respuesta paginada o directa
          if (Array.isArray(response.data.data.data)) {
            this.categories = response.data.data.data
          } else if (Array.isArray(response.data.data)) {
            this.categories = response.data.data
          }
        }
      } catch (error) {
        console.error('Error al cargar TO-DO:', error)
        this.$toast.error('Error al cargar las TO-DO')
      } finally {
        this.loading = false
      }
    },
    
    async saveCategory() {
      if (!this.$refs.form.validate()) return
      
      this.saving = true
      try {
        if (this.editedCategory.id) {
          await TodoCategoryService.put(this.editedCategory)
        } else {
          await TodoCategoryService.post(this.editedCategory)
        }
        
        this.$toast.success(
          this.editedCategory.id
            ? 'TO-DO actualizada correctamente'
            : 'TO-DO creada correctamente'
        )
        
        this.closeDialog()
        this.loadCategories()
      } catch (error) {
        console.error('Error al guardar TO-DO:', error)
        this.$toast.error('Error al guardar la TO-DO')
      } finally {
        this.saving = false
      }
    },
    
    confirmDelete(category) {
      this.categoryToDelete = category
      this.deleteDialog = true
    },
    
    async deleteCategory() {
      this.deleting = true
      try {
        await TodoCategoryService.delete(this.categoryToDelete.id)
        
        this.$toast.success('TO-DO eliminada correctamente')
        this.deleteDialog = false
        this.categoryToDelete = null
        this.loadCategories()
      } catch (error) {
        console.error('Error al eliminar TO-DO:', error)
        this.$toast.error('Error al eliminar la TO-DO')
      } finally {
        this.deleting = false
      }
    }
  },
  
  created() {
    this.loadCategories()
  }
}
</script>

<style scoped>
.category-card {
  transition: all 0.3s ease;
}

.category-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}
</style>
